import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Scheduler.css";
import { usePostHog } from 'posthog-js/react';

function Scheduler() {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  // Loading state for API data
  const [loading, setLoading] = useState(true);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [openToMoveEarlier, setOpenToMoveEarlier] = useState(false);

  // Retrieve bookingId from localStorage
  const bookingId = localStorage.getItem("bookingId");
  console.log("Got bookingId from localStorage: ", `${bookingId} .`)

  // State for the displayed month/year as a Date object
  const [displayedDate, setDisplayedDate] = useState(new Date());

  // Returns true if the date string (YYYY-MM-DD) is before today
  const isPastDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // midnight today
    const cellDate = new Date(dateStr);
    return cellDate < today;
  };

  // Arrays for month and year options
  const months = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" }
  ];

  const currentYear = new Date().getFullYear();
  const years = [];
  // Allow from last year to 5 years in the future
  for (let i = currentYear - 1; i <= currentYear + 5; i++) {
    years.push(i);
  }

  const posthog = usePostHog();

  // Track loading the scheduler
  useEffect(() => {
    if (posthog) {
      posthog.capture('scheduler_loaded');
    }
  }, [posthog]);

  // Handlers for dropdown changes
  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value, 10);
    const newDate = new Date(displayedDate.getFullYear(), newMonth, 1);
    setDisplayedDate(newDate);
    setSelectedDate("");
    setSelectedSlot(null);
  };

  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value, 10);
    const newDate = new Date(newYear, displayedDate.getMonth(), 1);
    setDisplayedDate(newDate);
    setSelectedDate("");
    setSelectedSlot(null);
  };

  // Fetch available times from API on mount
  useEffect(() => {

    const resumeSurvey = localStorage.getItem("resumeSurvey");      // "true" only when coming from RescheduleCancel
    const id = (localStorage.getItem("bookingId") || "").trim();
    if (resumeSurvey !== "true" && id) {
      console.debug("Scheduler: clearing stale bookingId", id);
      localStorage.removeItem("bookingId");
    }

    const userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};
    console.debug("Scheduler: Fetching available times from", `${API_URL}/available-times-location`, "with user info:", userInfo);

    fetch(`${API_URL}/available-times-location`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userInfo)
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailableTimes(data.times);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching times:", err);
        setLoading(false);
      });
  }, [API_URL]);

  // Filter available times to only those in the currently displayed month
  const groupedTimes = availableTimes.reduce((acc, slot) => {
    const slotDate = slot.date; // Expected format: "YYYY-MM-DD"
    const [year, month] = slotDate.split("-").map(Number);
    const displayedYear = displayedDate.getFullYear();
    const displayedMonth = displayedDate.getMonth() + 1;
    if (year === displayedYear && month === displayedMonth) {
      if (!acc[slotDate]) {
        acc[slotDate] = [];
      }
      acc[slotDate].push(slot);
    }
    return acc;
  }, {});

  const filteredGroupedTimes = groupedTimes;

  // Helper to format a date as "YYYY-MM-DD"
  const formatDate = (year, month, day) => {
    const m = month < 10 ? `0${month}` : month;
    const d = day < 10 ? `0${day}` : day;
    return `${year}-${m}-${d}`;
  };

  // Helper function to convert a date string to EST
  const convertToEST = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  // Day-of-week labels (Sunday-based)
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Compute month/day info
  const displayedYear = displayedDate.getFullYear();
  const displayedMonth = displayedDate.getMonth() + 1;
  const daysInMonth = new Date(displayedYear, displayedMonth, 0).getDate();

  // Compute day-of-week offset (for the 1st of the month)
  const firstDayOfMonth = new Date(displayedYear, displayedMonth - 1, 1).getDay();

  // Build calendarCells array: first add null for offset, then actual date strings.
  const calendarCells = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarCells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarCells.push(formatDate(displayedYear, displayedMonth, day));
  }

  // Chunk calendarCells into rows of 7
  const rows = [];
  for (let i = 0; i < calendarCells.length; i += 7) {
    rows.push(calendarCells.slice(i, i + 7));
  }

  // Compute today's date string (YYYY-MM-DD)
  const todayStr = new Date().toISOString().split("T")[0];

  // When an available date cell is clicked, select that date
  const handleSelectDate = (date) => {
    if (filteredGroupedTimes[date]) {
      setSelectedDate(date);
      setSelectedSlot(null); // Reset previous selection
      setBookingSuccess(false);
      if (posthog) {
        posthog.capture('scheduler_date_selected', { date });
      }
    }
  };

  // When a time slot is clicked, select it
  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    if (posthog) {
      posthog.capture('scheduler_time_selected', {
        date: selectedDate,
        time: convertToEST(slot.start_time), // Convert to EST
      });
    }
  };

  // Confirm the booking by calling the API and then navigating to confirmation
  const handleConfirmBooking = () => {
    if (!selectedSlot) return;
    setIsBooking(true);

    // Read a fresh flag from storage and normalize it
    const bookingId = (localStorage.getItem("bookingId") || "").trim();
    const isReschedule = localStorage.getItem("resumeSurvey") === "true";

    // Small helper to turn non-2xx responses into thrown errors
    const parseOrThrow = async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`);
      }
      return res.json();
    };

    if (bookingId && isReschedule) {
      // Reschedule flow
      const rescheduleData = {
        booking_id: bookingId,
        audit_time: selectedSlot.start_time,
        audit_date: selectedDate,
        open_to_move_earlier: openToMoveEarlier,
      };

      console.log(
        "Scheduler: Sending reschedule to",
        `${API_URL}/book-from-reschedule`,
        rescheduleData
      );

      fetch(`${API_URL}/book-from-reschedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rescheduleData),
      })
        .then(parseOrThrow)
        .then((responseData) => {
          console.log("Scheduler: Reschedule response:", responseData);

          // Also load userInfo so confirmation sees the same shape as new bookings
          const userInfoRaw = localStorage.getItem("userInfo");
          const userInfo = userInfoRaw ? JSON.parse(userInfoRaw) : {};

          // Clear BOTH flags so the next visit is treated as new
          localStorage.removeItem("bookingId");
          localStorage.removeItem("resumeSurvey");

          navigate("/confirmation", {
            state: {
              mode: "reschedule",
              appointment: { ...selectedSlot, date: selectedDate },
              userInfo,
              responseData
            },
          });

          if (posthog) {
            posthog.capture("reschedule_confirmed", {
              date: selectedDate,
              time: selectedSlot.start_time,
            });
          }
        })

        .catch((err) => console.error("Reschedule failed:", err))
        .finally(() => setIsBooking(false));

    } else {
      // New booking flow
      const userInfoRaw = localStorage.getItem("userInfo");
      const userInfo = userInfoRaw ? JSON.parse(userInfoRaw) : {};

      const bookingData = {
        ...userInfo,
        audit_time: selectedSlot.start_time,
        audit_date: selectedDate,
        open_to_move_earlier: openToMoveEarlier,
      };

      console.log("Scheduler: Sending booking to", `${API_URL}/book`, bookingData);

      fetch(`${API_URL}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingData),
      })
        .then(parseOrThrow)
        .then((responseData) => {
          console.log("Scheduler: Booking response:", responseData);

          // Clear any leftover flags
          localStorage.removeItem("bookingId");
          localStorage.removeItem("resumeSurvey"); // NEW

          navigate("/confirmation", {
            state: { mode: "new", appointment: { ...selectedSlot, date: selectedDate }, userInfo, responseData },
          });

          if (posthog) {
            posthog.capture("booking_confirmed", {
              date: selectedDate,
              time: selectedSlot.start_time,
            });
          }
        })

        .catch((err) => console.error("Booking failed:", err))
        .finally(() => setIsBooking(false));
    }
  };

  return (
    <div className="scheduler-container">
      {/* Top bar with company logo */}
      <div className="top-bar">
        <div className="logo">
          <Link to="https://www.colthomeservices.com/">
            <img src="/colt-single-logo.png" alt="Colt Home Services" />
          </Link>
        </div>
      </div>

      <div className="scheduler">
        <div className="scheduler-header">
          <h2 className="scheduler-title">Select an Appointment</h2>
          <div className="month-selector">
            <select value={displayedDate.getMonth()} onChange={handleMonthChange}>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select value={displayedDate.getFullYear()} onChange={handleYearChange}>
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-dial">
            <p>Loading calendar...</p>
          </div>
        ) : (
          <>
            {/* Day-of-week headers */}
            <div className="day-labels">
              {dayLabels.map((label) => (
                <div key={label} className="day-header">
                  {label}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="calendar-body">
              {rows.map((week, wIndex) => (
                <div key={wIndex} className="calendar-row">
                  {week.map((cell, cIndex) => {
                    if (!cell) {
                      // Render invisible cell for offset
                      return (
                        <div
                          key={`blank-${wIndex}-${cIndex}`}
                          className="day-cell blank-cell"
                          style={{ visibility: "hidden" }}
                        />
                      );
                    }
                    const isAvailable = filteredGroupedTimes[cell] && filteredGroupedTimes[cell].length > 0;
                    const isSelected = selectedDate === cell;
                    const isToday = cell === todayStr;
                    const isPast = isPastDate(cell);
                    return (
                      <div
                        key={cell}
                        className={`day-cell 
                          ${isAvailable && !isPast ? "available" : "unavailable"} 
                          ${isSelected ? "selected-date" : ""} 
                          ${isToday ? "today" : ""} 
                          ${isPast ? "past-date" : ""}`
                        }
                        onClick={() => isAvailable && !isPast && handleSelectDate(cell)}
                        style={isPast ? { cursor: "not-allowed", opacity: 0.5 } : {}}
                      >
                        {cell.split("-")[2]}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Time slots and selected slot display */}
            {selectedDate && (
              <div className="times-container">
                <div className="times-list">
                  <h3>Available Times for {selectedDate}</h3>
                  {filteredGroupedTimes[selectedDate] ? (
                    filteredGroupedTimes[selectedDate]
                      .slice()
                      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                      .map((slot) => (
                        <button
                          key={slot.start_time}
                          className={`time-slot ${selectedSlot && selectedSlot.start_time === slot.start_time ? "selected" : ""
                            }`}
                          onClick={() => handleSelectSlot(slot)}
                        >
                          {convertToEST(slot.start_time)} {/* Convert to EST */}
                          {slot.end_time &&
                            ` - ${convertToEST(slot.end_time)}`} {/* Convert to EST */}
                        </button>
                      ))
                  ) : (
                    <p>No available times for this date.</p>
                  )}
                </div>
                <div className="selected-time">
                  <h3>Selected Time</h3>
                  {selectedSlot ? (
                    <div className="time-detail selected">
                      {convertToEST(selectedSlot.start_time)} {/* Convert to EST */}
                      {selectedSlot.end_time &&
                        ` - ${convertToEST(selectedSlot.end_time)}`} {/* Convert to EST */}
                    </div>
                  ) : (
                    <p>Please select a time.</p>
                  )}
                </div>
              </div>
            )}

            {/* Checkbox for earlier appointment option */}
            {selectedSlot && (
              <div className="earlier-appointment-option">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={openToMoveEarlier}
                    onChange={(e) => setOpenToMoveEarlier(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  Would you like an earlier appointment if available? (You will be contacted directly)
                </label>
              </div>
            )}

            {/* Book button */}
            {selectedSlot && (
              <button className="confirm-button" onClick={handleConfirmBooking} disabled={isBooking}>
                {isBooking ? "Booking..." : "Book Appointment"}
              </button>
            )}

            {bookingSuccess && <p className="success-message">✅ Appointment booked successfully!</p>}
          </>
        )}
      </div>
    </div>
  );
}

export default Scheduler;
